import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogIn, ShieldAlert } from "lucide-react";
import { authApi, startLogin } from "../lib/auth";
import { LOGO_URL, BUSINESS_NAME } from "../lib/site";

// Renders children(user) when authenticated; otherwise a clean login screen.
export default function RequireAuth({ adminOnly = false, redirectTarget = "/pod-portal", children }) {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [status, setStatus] = useState(location.state?.user ? "ok" : "checking");

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setStatus("ok");
      return;
    }
    if (window.location.hash?.includes("session_id=")) {
      return; // AuthCallback will handle it
    }
    authApi
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("unauthenticated"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-slate-950 border-t-[#FFD400] rounded-full animate-spin" />
      </div>
    );
  }

  if (status === "unauthenticated") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border-2 border-slate-950 shadow-[8px_8px_0px_0px_rgba(255,212,0,0.8)] p-8 sm:p-10 max-w-md w-full text-center space-y-6">
          <img src={LOGO_URL} alt={BUSINESS_NAME} className="w-16 h-16 rounded-full border border-slate-900 mx-auto" />
          <div className="space-y-2">
            <h1 className="text-2xl font-extrabold uppercase tracking-tight">Brand Portal Login</h1>
            <p className="text-sm text-slate-600 font-medium">Sign in with Google to access your POD wallet, orders and transactions.</p>
          </div>
          <button
            type="button"
            data-testid="google-login-btn"
            onClick={() => startLogin(redirectTarget)}
            className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-6 py-4 border border-slate-950 transition-all"
          >
            <LogIn className="w-4 h-4" /> Continue with Google
          </button>
        </div>
      </div>
    );
  }

  if (adminOnly && user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border-2 border-slate-950 p-8 max-w-md w-full text-center space-y-4">
          <ShieldAlert className="w-12 h-12 text-slate-950 mx-auto" />
          <h1 className="text-xl font-extrabold uppercase">Access Restricted</h1>
          <p className="text-sm text-slate-600">This area is for administrators only. You are signed in as <strong>{user?.email}</strong>.</p>
        </div>
      </div>
    );
  }

  return children(user);
}
